import * as Sentry from '@sentry/browser';
import moment from 'moment';
import { call, delay, put, putResolve, select, spawn, takeEvery } from 'typed-redux-saga';

import { bffAPI, verificationAPI } from '@/api';
import { PhoneAPIModelStatusEnum } from '@/generated/api/fps/core';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import * as auth from '@/model/auth/actions';
import { makeSelectAuth, makeSelectInitialData } from '@/model/auth/selectors';
import * as global from '@/model/global/actions';
import { withGlobalLock } from '@/model/global/utils';
import createCache from '@/utils/cache';
import { ActionWithPayload, takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';
import { storeSavedPhone } from './actions';
import { makeSelectConfirmationState, makeSelectPhone } from './selectors';
import { ConfirmationState, IdentificationState, NAMESPACE } from './types';

const cache = createCache<{ termsOfServiceSigned: boolean }>('MNX_WALLET_AGREEMENTS');

const selectPhone = makeSelectPhone();
const selectAuth = makeSelectAuth();
const selectAuthData = makeSelectInitialData();
const selectConfirmationState = makeSelectConfirmationState();

const calculateState = ({ attempt, validTill }: { attempt: number; validTill: Date }): ConfirmationState => {
  if (new Date().getTime() > validTill.getTime()) {
    return ConfirmationState.EXPIRED;
  }
  if (attempt > 3) {
    return ConfirmationState.ATTEMPTS_NUMBER_EXHAUSTED;
  }
  return ConfirmationState.IN_PROGRESS;
};

function* init(): Generator {
  try {
    const { success } = yield* select(selectAuth);
    if (!success) {
      yield* putResolve(actions.storeInitialization({}).pure);
      return;
    }
    let state: actions.StoreInitializationActionPayload = {};

    const authData = yield* select(selectAuthData);

    if (authData?.phone) {
      state = {
        ...state,
        phone: {
          value: authData.phone.number,
        },
        profileIdentificationStatus: authData.status,
        ...(authData.phone.status === PhoneAPIModelStatusEnum.Verified
          ? {
              confirmation: {
                state: ConfirmationState.PASSED,
                phone: authData.phone.number,
              },
              identificationState: IdentificationState.PASSED,
            }
          : {
              identificationState: IdentificationState.NOT_PASSED,
            }),
      };
      if (state.confirmation?.state !== ConfirmationState.PASSED) {
        if (authData?.confirmation) {
          state = {
            ...state,
            confirmation: {
              phone: authData.phone.number,
              validTill: authData.confirmation.validTill,
              resendAfter: authData.confirmation.resendAfter,
              attempt: authData.confirmation.attempt,
              state: calculateState({
                validTill: authData.confirmation.validTill,
                attempt: authData.confirmation.attempt,
              }),
            },
          };
        }
      }
    }
    yield* putResolve(actions.storeInitialization(state).pure);
    if (state.confirmation?.state === ConfirmationState.IN_PROGRESS) {
      yield* spawn(startConfirmationTimer);
    }
  } finally {
    yield put(actions.notifyInitializationFinished().pure);
  }
}

let timerInProgress = false;

function* startConfirmationTimer(): Generator {
  if (timerInProgress) {
    return;
  }
  try {
    timerInProgress = true;
    while (true) {
      const state = yield* select(selectConfirmationState);
      if (
        // prettier-ignore
        // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
        !state
        || !state.validTill
        || state?.state === ConfirmationState.PASSED
        || state?.state === ConfirmationState.ATTEMPTS_NUMBER_EXHAUSTED
      ) {
        break;
      }
      if (state.validTill.getTime() <= new Date().getTime()) {
        goalReached(YMGoals.PHONE_CONFIRMATION_TIMEOUT_CODE);
        yield* put(actions.storeConfirmationState({ phone: state.phone, state: ConfirmationState.EXPIRED }).pure);
        break;
      }
      yield* delay(1000);
    }
  } finally {
    timerInProgress = false;
  }
}

function* updatePhoneAndRequestConfirm(): Generator {
  const confirmationState = yield* select(selectConfirmationState);
  const phone = (yield* select(selectPhone))?.value;
  if (
    // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
    // prettier-ignore
    phone !== confirmationState?.phone
    || (confirmationState?.validTill && moment(confirmationState.validTill)
      .isBefore())
  ) {
    if (!phone) {
      // to make ts happier
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('invalid state: phone or type are unknown');
    }
    yield* putResolve(storeSavedPhone({ phone }).pure);
    yield* call(sendConfirmationRequest);
  }
}

function* sendConfirmationRequest(): Generator {
  try {
    const phone = (yield* select(selectPhone))?.value;
    if (!phone) {
      // to make ts happier
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('invalid state: phone or type are unknown');
    }
    const { validTill, resendAfter } = yield* call(bffAPI.updatePhone, { phone });
    yield* putResolve(
      actions.storeConfirmationState({
        phone,
        state: ConfirmationState.IN_PROGRESS,
        validTill,
        resendAfter,
      }).pure,
    );
    yield* spawn(startConfirmationTimer);
  } catch (e) {
    if (e.response?.data?.code !== 'ALREADY_SENT') {
      // FIXME: how to know validTill?
      throw e;
    }
  }
}

function* checkConfirmationCode({
  payload: { code },
}: ActionWithPayload<actions.CheckConfirmationActionPayload>): Generator {
  const state = yield* select(selectConfirmationState);
  // to make TS happy
  if (!state?.phone) {
    throw new Error('No phone');
  }
  const { phone } = state;
  try {
    yield* call(bffAPI.confirmPhone, { code, phone });
    try {
      const profileStatus = yield* call(verificationAPI.queryVerificationStatus);
      yield* putResolve(actions.storeProfileIdentificationStatus(profileStatus).pure);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e instanceof Error ? e : new Error());
    }
    yield* putResolve(actions.storeConfirmationState({ state: ConfirmationState.PASSED, phone }).pure);
    yield* putResolve(actions.storeIdentificationState({ state: IdentificationState.PASSED }).pure);
    yield* put(actions.notifyIdentificationPassed().pure);
    goalReached(YMGoals.PHONE_CONFIRMATION_FINISHED);
  } catch (e) {
    if (e.response?.data?.code === 'check_limit') {
      const confirmationState = yield* select(selectConfirmationState);
      yield* putResolve(
        actions.storeConfirmationState({
          phone,
          resendAfter: confirmationState?.resendAfter,
          state: ConfirmationState.ATTEMPTS_NUMBER_EXHAUSTED,
        }).pure,
      );
    } else if (e.response?.data?.code === 'invalid_confirmation_code') {
      const confirmationState = yield* select(selectConfirmationState);
      yield* putResolve(
        actions.storeConfirmationState({
          ...confirmationState,
          phone,
          state: ConfirmationState.WRONG_CODE,
          attempt: (confirmationState?.attempt || 0) + 1,
        }).pure,
      );
    }

    throw e;
  }
}

function* logout(): Generator {
  cache.clear();
  yield* put(actions.storeLogout().pure);
}

export default function* rootSaga(): Generator {
  yield* takeEvery(auth.notifyAuthorizationFinished.type, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEvery(global.notifyLogout.type, withGlobalLock(logout, 'common.messages.operations.logout'));
  yield* takeEveryTypesafe(
    actions.updatePhoneAndRequestConfirm,
    withGlobalLock(updatePhoneAndRequestConfirm, 'identification.messages.operations.phone.update'),
    function* onError(): Generator {
      yield* put(
        global.addNotification({
          type: NAMESPACE,
          messageId: 'confirmation.errors.send.error',
          severity: 'error',
        }).pure,
      );
    },
  );
  yield* takeEveryTypesafe(
    actions.sendConfirmationRequest,
    withGlobalLock(sendConfirmationRequest, 'identification.messages.operations.code.send'),
    function* onError(): Generator {
      yield* put(
        global.addNotification({
          type: NAMESPACE,
          messageId: 'confirmation.errors.send.error',
          severity: 'error',
        }).pure,
      );
    },
  );
  yield* takeEveryTypesafe(
    actions.checkConfirmationCode,
    withGlobalLock(checkConfirmationCode, 'identification.messages.operations.code.check'),
    function* onError(error): Generator {
      let messageId;
      if (error.response?.data?.code === 'check_limit') {
        messageId = 'confirmation.errors.attempts.number.exhausted';
      } else if (error.response?.data?.code === 'invalid_confirmation_code') {
        messageId = 'confirmation.errors.wrong.code';
      } else {
        messageId = 'confirmation.errors.check.error';
      }
      yield* put(
        global.addNotification({
          type: NAMESPACE,
          messageId,
          severity: 'error',
        }).pure,
      );
    },
  );
}
